import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IconPlus, IconMinus } from './Icon'
import { dayOptions, monthOptions, yearOptions } from '../../jpe-top-page/utils'
import { useLastMinuteInquiryStore } from '../../../store/lastMinuteInquiryStore'

type LastMinuteInquiryModalProps = {
  onClose: () => void
}

export const LastMinuteInquiryModal = ({ onClose }: LastMinuteInquiryModalProps) => {
  const { t } = useTranslation()
  const DAY_OPTIONS = useMemo(() => dayOptions(), [])
  const MONTH_OPTIONS = useMemo(() => monthOptions(t), [t])
  const YEAR_OPTIONS = useMemo(() => yearOptions(), [])
  const formData = useLastMinuteInquiryStore((state) => state.formData)
  const errors = useLastMinuteInquiryStore((state) => state.errors)
  const isSubmitting = useLastMinuteInquiryStore((state) => state.isSubmitting)
  const isSubmitted = useLastMinuteInquiryStore((state) => state.isSubmitted)
  const updateFormData = useLastMinuteInquiryStore((state) => state.updateFormData)
  const handleCountChange = useLastMinuteInquiryStore((state) => state.handleCountChange)
  const setBirthdayAt = useLastMinuteInquiryStore((state) => state.setBirthdayAt)
  const setStudyAbroadChildAt = useLastMinuteInquiryStore((state) => state.setStudyAbroadChildAt)
  const submitLastMinuteInquiry = useLastMinuteInquiryStore((state) => state.submitLastMinuteInquiry)

  const handleSubmit = async () => {
    await submitLastMinuteInquiry()
  }

  return (
    <div className="modal-backdrop fixed top-0 left-0 right-0 z-50 w-full h-full flex justify-center md:items-center p-3 md:p-6 md:inset-0">
      <div
        className="flex flex-col items-start bg-white rounded-lg w-[350px] md:w-[580px] overflow-y-scroll"
        style={{ animation: '0.4s ease-in-out 0s 1 normal forwards running slide-down', height: '80vh' }}
      >
        {!isSubmitted ? (
          <div className="w-full flex flex-col">
            <div className="flex items-start justify-between gap-2 p-5 border-b border-gray-200">
              <h3 className="font-bold font-size-16 leading-6 text-black-900">締め切り後の予約リクエストのご希望について</h3>
              <button
                className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
                type="button"
                style={{ marginTop: '-8px', marginRight: '-12px' }}
                onClick={onClose}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto">
              <div className="mx-5 py-5 border-b border-b border-gray-200">
                <div className="font-size-13 leading-5">
                  {/* [#2658] 締切日数は部屋ごとに異なる（清水町は60日前）ため、文言に日数を含めない方針。
                      Figma: https://www.figma.com/design/LxsTPD46nua5B6fVk5jqAR/UIUX?node-id=35712-27561 */}
                  <p className="mb-3">
                    ご希望の日程は、通常のご予約受付期間を過ぎております。締め切り後のお申し込みは「お問い合わせ」として承り、園の受け入れ状況を個別に確認したうえで、ご予約リクエストをお受けできるかをご案内いたします。
                    <br />
                    下記の事項すべてにご同意いただける方のみ、お問い合わせをお願いいたします。
                  </p>
                  <p className="font-bold text-black-900 mb-2">ご同意いただきたい内容</p>
                  <ul className="mb-2">
                    <li className="ml-3">
                      滞在開始前に、園とのオンライン面談を必ず実施していただくこと（平日日中のご対応をお願いする場合がございます）
                    </li>
                    <li className="ml-3">
                      直前でのご予約の場合、ご予約確定後は、マイページの「やることリスト」（児童情報の登録・園への書類提出など）を速やかに完了していただくこと
                    </li>
                    <li className="ml-3">ご予約確定後のキャンセルには、キャンセルポリシーに基づくキャンセル料が生じること</li>
                  </ul>
                  <p className="mt-3">
                    ご不明な点がございましたら、お気軽にお問い合わせください。みなさまの保育園留学が素敵な体験となるよう、しっかりとサポートいたします。
                  </p>
                </div>

                <label className="flex items-center gap-3 pt-4 font-size-14 text-black-900 font-bold">
                  <input
                    type="checkbox"
                    checked={formData.agreement}
                    className="form-checkbox rounded"
                    onChange={(e) => updateFormData({ agreement: e.target.checked })}
                  />
                  上記の内容に同意する
                </label>
              </div>

              <div className="px-5 pt-4 pb-5 space-y-4 text-sm">
                <p className="font-size-13 text-black-600">留学施設・宿泊施設への確認に必要な情報をご入力ください。</p>
                <div className="mb-2">
                  <label className="block font-size-14 text-black-900 font-bold">
                    代表者名
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    className="text-sm h-10 w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 input-check"
                    value={formData.representativeName}
                    onChange={(e) => updateFormData({ representativeName: e.target.value })}
                  />
                  {errors.representativeName && <p className="text-red-500 text-xs mt-1">{errors.representativeName}</p>}
                </div>

                <div className="mb-2">
                  <label className="block font-size-14 text-black-900 font-bold">
                    メールアドレス
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    className="text-sm h-10 w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 input-check"
                    type="email"
                    placeholder="example@email.com"
                    value={formData.email}
                    onChange={(e) => updateFormData({ email: e.target.value })}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div className="rounded-lg border border-gray-200 p-3 font-size-12 text-black-600 leading-6">
                  <p className="font-bold font-size-14 mb-2 text-black-900">人数</p>
                  <div className="w-full grid grid-cols-12 mb-2">
                    <div className="col-start-1 col-span-8 text-left">
                      <div className="title font-bold font-size-14 text-black-900">大人</div>
                    </div>
                    <div className="col-start-9 col-span-4 flex items-center gap-2 h-8 ml-auto">
                      <button
                        onClick={() => handleCountChange('numberOfAdults', 'down')}
                        className="all-unset div rounded-full cursor-pointer select-none rounded-bl-full disabled:opacity-30"
                        disabled={formData.numberOfAdults <= 1}
                        type="button"
                      >
                        <IconMinus />
                      </button>
                      <div className="text-center mt-1 w-6 font-size-16">{formData.numberOfAdults}</div>
                      <button
                        onClick={() => handleCountChange('numberOfAdults', 'up')}
                        className="all-unset rounded-full cursor-pointer rounded-bl-full"
                        type="button"
                      >
                        <IconPlus />
                      </button>
                    </div>
                  </div>

                  <div className="title font-bold font-size-14 text-black-900">子ども</div>
                  <div className="w-full grid grid-cols-12 mt-3 mb-3 items-center">
                    <div className="col-start-1 col-span-8 text-left">
                      <div className="font-size-14 text-black-900">留学あり</div>
                    </div>
                    <div className="col-start-9 col-span-4 flex items-center gap-2 h-8 ml-auto">
                      <button
                        onClick={() => handleCountChange('numberOfStudyAbroad', 'down')}
                        className="div rounded-full cursor-pointer select-none disabled:opacity-30"
                        disabled={formData.numberOfStudyAbroad <= 1}
                        type="button"
                      >
                        <IconMinus />
                      </button>
                      <div className="text-center mt-1 w-6 font-size-16">{formData.numberOfStudyAbroad}</div>
                      <button
                        onClick={() => handleCountChange('numberOfStudyAbroad', 'up')}
                        className="all-unset cursor-pointer"
                        type="button"
                      >
                        <IconPlus />
                      </button>
                    </div>
                  </div>

                  {Array.from({ length: formData.numberOfStudyAbroad }).map((_, idx) => {
                    const birthday = formData.birthdays[idx] || { year: '', month: '', day: '' }
                    return (
                      <div key={idx} className="mt-3">
                        <div className="font-size-13 mb-1">
                          {idx + 1}人目の生年月日
                          <span className="text-red-500">*</span>
                        </div>
                        <div className="flex items-center">
                          <select
                            className="date-selection-year text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 border-gray-300 mt-2 select-check date-selection md:w-[143px]"
                            value={birthday.year}
                            onChange={(e) => setBirthdayAt(idx, { ...birthday, year: e.target.value })}
                          >
                            <option value="">生年</option>
                            {YEAR_OPTIONS.map((year) => (
                              <option key={year.value} value={year.value}>
                                {year.label}
                              </option>
                            ))}
                          </select>
                          <span className="px-2 mt-auto">年</span>

                          <select
                            className="date-selection-month text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 border-gray-300 mt-2 select-check date-selection w-[70px]"
                            value={birthday.month}
                            onChange={(e) => setBirthdayAt(idx, { ...birthday, month: e.target.value })}
                          >
                            <option value="">月</option>
                            {MONTH_OPTIONS.map((month) => (
                              <option key={month.value} value={month.value}>
                                {month.value}
                              </option>
                            ))}
                          </select>
                          <span className="px-2 mt-auto">月</span>

                          <select
                            className="date-selection-day text-sm text-gray-900 pl-2 py-3 rounded-lg bg-gray-50 border-gray-300 mt-2 select-check date-selection w-[70px]"
                            value={birthday.day}
                            onChange={(e) => setBirthdayAt(idx, { ...birthday, day: e.target.value })}
                          >
                            <option value="">日</option>
                            {DAY_OPTIONS.map((day) => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))}
                          </select>
                          <span className="pl-2 mt-auto">日</span>
                        </div>
                        {errors.birthdays?.[idx] && <p className="text-red-500 text-xs mt-2">{errors.birthdays[idx]}</p>}
                      </div>
                    )
                  })}

                  <div className="w-full grid grid-cols-12 mt-3 mb-3 items-center">
                    <div className="col-start-1 col-span-8 text-left">
                      <div className="font-size-14 text-black-900">留学なし（未就学児）</div>
                    </div>
                    <div className="col-start-9 col-span-4 flex items-center gap-2 h-8 ml-auto">
                      <button
                        onClick={() => handleCountChange('numberOfPreschoolers1', 'down')}
                        className="div rounded-full cursor-pointer select-none disabled:opacity-30"
                        disabled={formData.numberOfPreschoolers1 <= 0}
                        type="button"
                      >
                        <IconMinus />
                      </button>
                      <div className="text-center mt-1 w-6 font-size-16">{formData.numberOfPreschoolers1}</div>
                      <button
                        onClick={() => handleCountChange('numberOfPreschoolers1', 'up')}
                        className="all-unset cursor-pointer"
                        type="button"
                      >
                        <IconPlus />
                      </button>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-12 mt-3 mb-3 items-center">
                    <div className="col-start-1 col-span-8 text-left">
                      <div className="font-size-14 text-black-900">留学なし（小学生）</div>
                    </div>
                    <div className="col-start-9 col-span-4 flex items-center gap-2 h-8 ml-auto">
                      <button
                        onClick={() => handleCountChange('numberOfStudents1', 'down')}
                        className="div rounded-full cursor-pointer select-none disabled:opacity-30"
                        disabled={formData.numberOfStudents1 <= 0}
                        type="button"
                      >
                        <IconMinus />
                      </button>
                      <div className="mt-1 w-6 text-center font-size-16">{formData.numberOfStudents1}</div>
                      <button
                        onClick={() => handleCountChange('numberOfStudents1', 'up')}
                        className="all-unset cursor-pointer"
                        type="button"
                      >
                        <IconPlus />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {Array.from({ length: formData.numberOfStudyAbroad }).map((_, idx) => {
                    const child = formData.studyAbroadChildren[idx] || {
                      allergyAvailability: '',
                      allergyDetail: '',
                      schoolExperience: '',
                      supportNeeds: '',
                      supportNeedsDetail: '',
                    }
                    const childErrors = errors.studyAbroadChildren?.[idx] || {}
                    return (
                      <div key={idx} className="mb-4">
                        <p className="font-bold font-size-14 text-black-900 mb-2">{idx + 1}人目</p>
                        <div className="mb-2">
                          <label className="block font-size-14 text-black-900 font-bold mb-2">
                            アレルギーはありますか
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <select
                            className="w-full text-sm text-gray-900 px-3 py-3 rounded-lg bg-gray-50 border-gray-300 select-check"
                            value={child.allergyAvailability}
                            onChange={(e) => setStudyAbroadChildAt(idx, { allergyAvailability: e.target.value })}
                          >
                            <option value="">選択してください</option>
                            <option value="有">有</option>
                            <option value="無">無</option>
                          </select>
                          {childErrors.allergyAvailability && (
                            <p className="text-red-500 text-xs mt-1">{childErrors.allergyAvailability}</p>
                          )}
                        </div>

                        {child.allergyAvailability === '有' && (
                          <div className="mb-2">
                            <label className="block font-size-14 text-black-900 font-bold">
                              アレルギーの症状・詳細
                              <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                              className="text-sm h-10 w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 my-2 input-check"
                              value={child.allergyDetail}
                              onChange={(e) => setStudyAbroadChildAt(idx, { allergyDetail: e.target.value })}
                            />
                            {childErrors.allergyDetail && (
                              <p className="text-red-500 text-xs mt-1">{childErrors.allergyDetail}</p>
                            )}
                          </div>
                        )}

                        <div className="mb-2">
                          <label className="block font-size-14 text-black-900 font-bold">
                            今まで、週3日以上の通園経験はありますか
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <select
                            className="w-full text-sm text-gray-900 px-3 py-3 rounded-lg bg-gray-50 border-gray-300 my-2 select-check"
                            value={child.schoolExperience}
                            onChange={(e) => setStudyAbroadChildAt(idx, { schoolExperience: e.target.value })}
                          >
                            <option value="">選択してください</option>
                            <option value="有">有</option>
                            <option value="無">無</option>
                          </select>
                          {childErrors.schoolExperience && (
                            <p className="text-red-500 text-xs mt-1">{childErrors.schoolExperience}</p>
                          )}
                        </div>

                        <div className="mb-2">
                          <label className="block font-size-14 text-black-900 font-bold">
                            発達の遅れや集団生活を送る上で配慮が必要なことはありますか
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <select
                            className="w-full text-sm text-gray-900 px-3 py-3 rounded-lg bg-gray-50 border-gray-300 mt-2 select-check"
                            value={child.supportNeeds}
                            onChange={(e) => setStudyAbroadChildAt(idx, { supportNeeds: e.target.value })}
                          >
                            <option value="">選択してください</option>
                            <option value="有">有</option>
                            <option value="無">無</option>
                          </select>
                          {childErrors.supportNeeds && <p className="text-red-500 text-xs mt-1">{childErrors.supportNeeds}</p>}
                        </div>

                        {child.supportNeeds === '有' && (
                          <div className="mb-2">
                            <label className="block font-size-14 text-black-900 font-bold">
                              詳細
                              <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                              className="text-sm h-10 w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 input-check"
                              value={child.supportNeedsDetail}
                              onChange={(e) => setStudyAbroadChildAt(idx, { supportNeedsDetail: e.target.value })}
                            />
                            {childErrors.supportNeedsDetail && (
                              <p className="text-red-500 text-xs mt-1">{childErrors.supportNeedsDetail}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="px-5 pb-5 pt-4">
              <button
                type="button"
                className={`box-border h-11 px-3 py-2 w-full rounded-lg text-white font-size-14 font-bold ${
                  formData.agreement ? 'bg-green' : 'bg-gray-300 cursor-not-allowed'
                }`}
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.agreement}
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
              <p className="text-center font-size-12 mt-3 mb-5" style={{ color: '#99A1AF' }}>
                同意チェックボックスにチェックを入れると送信できます
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full p-5">
            <div className="flex justify-between mb-4">
              <div className="font-bold font-size-16 text-black-900">お問い合わせを受け付けました</div>
              <button
                className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
                type="button"
                style={{ marginTop: '-8px', marginRight: '-12px' }}
                onClick={onClose}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <p className="text-sm leading-6 text-black-600">
              ご入力ありがとうございます。
              <br />
              留学施設・宿泊施設に確認のうえ、3営業日以内に入力いただいたメールアドレスへご連絡いたします。
              <br />
              ⚠️ まだご予約は完了していません。専用予約URLをお送りいたしますので、今しばらくお待ちくださいませ。
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
